import type moment from 'moment'

import {
  add,
  clone,
  diff,
  format,
  hour,
  isAfter,
  isoWeekday,
  local,
  minute,
  now,
  second,
  set,
  startOf,
  subtract,
  utc,
} from 'app/lib/date'

type DateValue = moment.MomentInput
type DateMoment = moment.Moment

interface OpeningHoursUTCEntry {
  hour: string
  negativeDayOffset: number
}

interface OpeningHoursUTC {
  start: OpeningHoursUTCEntry
  end: OpeningHoursUTCEntry
}

interface TimeSlotValue {
  startTime: string
  endTime: string
}

interface TimeSlots {
  timeSlotsValues: TimeSlotValue[]
  timeSlotsDisplays: string[]
}

export const openingHoursInUTC = {
  weekday: {
    start: {
      hour: '22',
      negativeDayOffset: 1,
    },
    end: {
      hour: '09',
      negativeDayOffset: 0,
    },
  },
  saturday: {
    start: {
      hour: '23',
      negativeDayOffset: 1,
    },
    end: {
      hour: '06',
      negativeDayOffset: 0,
    },
  },
}

export const currentTime = (): DateMoment => now()

export const dateIsSaturday = (date: DateValue): boolean =>
  isoWeekday(date) === 6

export const convertUTCOpeningHoursToLocal = (
  openingHoursUTC: OpeningHoursUTCEntry,
  referenceTime: DateValue = currentTime(),
): DateMoment => {
  const { hour: openingHour, negativeDayOffset } = openingHoursUTC
  const dateOffsetToUTC = format(
    subtract(referenceTime, negativeDayOffset, 'day'),
    'YYYY-MM-DD',
  )

  return local(`${dateOffsetToUTC}T${openingHour}:00:00Z`)
}

export const availableTimeSlots = (
  startTime: DateValue,
  endTime: DateValue,
): number => Math.floor(Math.abs(diff(startTime, endTime, 'minutes')) / 30)

export const plusMinutes = (
  time: DateValue,
  delayMinutes: number,
): DateMoment => add(time, delayMinutes, 'minutes')

export const clearTimeToLastHour = (time: DateValue): DateMoment => {
  let timeCopy = clone(time)
  timeCopy = minute(timeCopy, 0)
  timeCopy = second(timeCopy, 0)
  return set(timeCopy, 'millisecond', 0)
}

export const prepTimeSlots = (
  openingHoursUTC: OpeningHoursUTC,
  isToday: boolean,
  referenceTime: DateValue = currentTime(),
): TimeSlots => {
  const timeSlotsValues: TimeSlotValue[] = []
  const timeSlotsDisplays: string[] = []
  const { start, end } = openingHoursUTC
  let startTime = convertUTCOpeningHoursToLocal(start, referenceTime)
  const endTime = convertUTCOpeningHoursToLocal(end, referenceTime)

  if (isToday) {
    startTime = plusMinutes(clearTimeToLastHour(referenceTime), 60)
  }

  const timeSlotsAvailable = availableTimeSlots(startTime, endTime)
  let lastTimeSlot = startTime

  for (let i = 0; i < timeSlotsAvailable; i += 1) {
    const currentTimeSlot = add(lastTimeSlot, 30, 'minutes')
    timeSlotsValues.push({
      startTime: format(currentTimeSlot, 'YYYY-MM-DD HH:mm'),
      endTime: format(add(currentTimeSlot, 30, 'minutes'), 'YYYY-MM-DD HH:mm'),
    })
    timeSlotsDisplays.push(
      `${format(currentTimeSlot, 'h:mm')} - ${format(
        add(currentTimeSlot, 30, 'minutes'),
        'h:mm A',
      )}`,
    )
    lastTimeSlot = currentTimeSlot
  }

  return { timeSlotsValues, timeSlotsDisplays }
}

export const timeSlotsGenerator = (
  date: DateValue,
  isToday: boolean,
  referenceTime: DateValue = currentTime(),
): TimeSlots =>
  dateIsSaturday(date)
    ? prepTimeSlots(openingHoursInUTC.saturday, isToday, referenceTime)
    : prepTimeSlots(openingHoursInUTC.weekday, isToday, referenceTime)

export const isDateSunday = (date: DateValue): boolean => isoWeekday(date) === 7

export const datesSlotsGenerator = (
  firstDisplayDate: DateValue,
): {
  datesAvailableDisplays: string[]
  datesAvailableValues: DateMoment[]
} => {
  const datesAvailableDisplays: string[] = []
  const datesAvailableValues: DateMoment[] = []
  let currentDay = clone(firstDisplayDate)

  for (let i = 0; i < 14; i += 1) {
    if (isDateSunday(currentDay)) {
      i -= 1
    } else {
      datesAvailableValues.push(currentDay)
      datesAvailableDisplays.push(format(currentDay, 'dddd, MMMM Do'))
    }
    currentDay = add(currentDay, 1, 'days')
  }

  return { datesAvailableValues, datesAvailableDisplays }
}

export const firstDisplayDateFrom = (
  localTimeNow: DateValue,
  referenceTime: DateValue = localTimeNow,
): DateMoment => {
  const localTimeNowCopy = clone(localTimeNow)
  const saturdayEndTimeInLocal = convertUTCOpeningHoursToLocal(
    openingHoursInUTC.saturday.end,
    referenceTime,
  )

  if (
    dateIsSaturday(localTimeNowCopy) &&
    isAfter(localTimeNowCopy, utc(saturdayEndTimeInLocal))
  ) {
    return startOf(add(localTimeNowCopy, 1, 'days'), 'day')
  }

  const weekdayUTCEndTime = convertUTCOpeningHoursToLocal(
    openingHoursInUTC.weekday.end,
    referenceTime,
  )

  if (isAfter(localTimeNowCopy, utc(weekdayUTCEndTime))) {
    return startOf(add(localTimeNowCopy, 1, 'days'), 'day')
  }

  return startOf(localTimeNowCopy, 'day')
}

export const getDate = (time: DateValue): string => format(time, 'YYYY-MM-DD')

export const composePayLoadTime = (
  chosenDateDisplayIndex: number,
  datesValues: DateMoment[],
  chosenTimeDisplayIndex: number,
  timeSlotsValues: TimeSlotValue[],
  type: keyof TimeSlotValue,
): DateMoment => {
  const chosenTimeSlot = timeSlotsValues[chosenTimeDisplayIndex]
  const chosenDate = datesValues[chosenDateDisplayIndex]

  if (!chosenTimeSlot || !chosenDate) {
    throw new Error('Unable to compose callback payload time')
  }

  const chosenHour = hour(chosenTimeSlot[type])
  const chosenMinute = minute(chosenTimeSlot[type])
  let composedDateTime = clone(chosenDate)
  composedDateTime = hour(composedDateTime, chosenHour)
  composedDateTime = minute(composedDateTime, chosenMinute)
  composedDateTime = second(composedDateTime, 0)
  return set(composedDateTime, 'millisecond', 0)
}

export const formatCallbackPayloadTime = (time: DateValue): string =>
  format(time, 'YYYY-MM-DDTHH:mm:ssZ')
